import React, { useState } from 'react';
import { Check, X, RotateCcw, Info, AlertTriangle, CheckCircle2, Eye, TriangleAlert } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O8 · Séquence des contrôles avant chaque changement
//   Scénario A · Changement d'ALLURE (accélérer/ralentir) :
//       1. Rétro intérieur
//   Scénario B · Changement de VOIE (dépassement / rabattement) :
//       1. Rétro intérieur → 2. Rétro extérieur du côté → 3. Angle mort du côté →
//       4. Clignotant → 5. Positionnement (décalage progressif)
//   L'utilisateur clique dans l'ORDRE. Un clic hors ordre = alerte rouge.
// ══════════════════════════════════════════════════════════════════════════

const SCENARIOS = {
  allure: {
    label: "Changement d'ALLURE",
    color: '#0ea5e9',
    context: "Tu vas ralentir ou accélérer sur ta voie actuelle. Un SEUL contrôle est requis.",
    sequence: [
      { id: 'ri', label: 'Rétro intérieur', desc: "Vérifier qui te suit et à quelle distance. Si un véhicule colle, ralentir plus progressivement.", pos: { x: 400, y: 130 } },
    ],
  },
  voie: {
    label: "Changement de VOIE (dépassement)",
    color: '#f59e0b',
    context: "Tu vas changer de file (dépassement, insertion, rabattement). 5 contrôles OBLIGATOIRES dans l'ordre.",
    sequence: [
      { id: 'ri',  label: '1. Rétro intérieur',      desc: "Qui me suit ? À quelle distance ?",             pos: { x: 400, y: 130 } },
      { id: 'reg', label: '2. Rétro extérieur GAUCHE', desc: "Quelqu'un dans la voie de gauche ? Une moto qui remonte en interfile ?", pos: { x: 220, y: 200 } },
      { id: 'am',  label: '3. Angle mort GAUCHE',    desc: "Contrôle-épaule GAUCHE (regard rapide par-dessus l'épaule).", pos: { x: 130, y: 320 } },
      { id: 'cli', label: '4. Clignotant GAUCHE',    desc: "Signaler mon intention AU MOINS 3 secondes avant.",          pos: { x: 270, y: 400 } },
      { id: 'pos', label: '5. Positionnement',       desc: "Décaler progressivement (jamais brutalement) vers la voie de gauche.", pos: { x: 500, y: 480 } },
    ],
  },
};

export default function ControlesSequenceSvg() {
  const [scenario, setScenario] = useState('voie');
  const [done, setDone] = useState([]);
  const [error, setError] = useState(null);

  const sc = SCENARIOS[scenario];
  const nextExpected = sc.sequence[done.length];
  const isComplete = done.length === sc.sequence.length;

  const clickStep = (id) => {
    if (!nextExpected) return;
    if (nextExpected.id === id) {
      setDone([...done, id]);
      setError(null);
    } else {
      setError(`Non ! L'ordre est : ${nextExpected.label}`);
      setTimeout(() => setError(null), 2500);
    }
  };

  const reset = () => { setDone([]); setError(null); };

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
          {Object.entries(SCENARIOS).map(([k, s], i) => (
            <button key={k} onClick={() => { setScenario(k); reset(); }}
              data-testid={`o8-scenario-${k}`}
              className={`h-9 px-3.5 text-[11.5px] font-bold font-mono-spec ${scenario === k ? 'text-white' : 'text-white/60'} ${i > 0 ? 'border-l border-white/15' : ''}`}
              style={scenario === k ? { background: s.color + '25' } : {}}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button onClick={reset} data-testid="o8-reset" className="ml-auto h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[11.5px] font-semibold inline-flex items-center gap-1">
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label={sc.label}>
            <defs>
              <linearGradient id="o8-dash" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0b1120" />
              </linearGradient>
              <linearGradient id="o8-wheel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f3f46" />
                <stop offset="100%" stopColor="#18181b" />
              </linearGradient>
            </defs>

            {/* Fond cockpit */}
            <rect width={800} height={700} fill="#0b1120" />
            <path d="M 0 0 L 800 0 L 780 180 L 20 180 Z" fill="#1e3a5f" opacity={0.7} />
            <path d="M 50 60 L 200 40 L 600 40 L 750 60 L 720 160 L 80 160 Z" fill="#3b5f80" opacity={0.5} />
            <path d="M 0 180 L 800 180 L 800 280 L 0 280 Z" fill="url(#o8-dash)" />

            {/* Volant */}
            <g transform="translate(400 420)">
              <ellipse cx={0} cy={0} rx={110} ry={80} fill="none" stroke="url(#o8-wheel)" strokeWidth={16} />
              <ellipse cx={0} cy={0} rx={28} ry={22} fill="#18181b" stroke="#71717a" strokeWidth={2} />
              <text y={4} textAnchor="middle" fill="#71717a" fontSize={12} fontWeight="900" fontFamily="serif">CV</text>
              <line x1={-28} y1={0} x2={-110} y2={0} stroke="#3f3f46" strokeWidth={5} />
              <line x1={28} y1={0} x2={110} y2={0} stroke="#3f3f46" strokeWidth={5} />
              <line x1={0} y1={22} x2={0} y2={80} stroke="#3f3f46" strokeWidth={5} />
            </g>

            {/* Rétro intérieur (haut centre) */}
            <g transform="translate(400 130)" onClick={() => clickStep('ri')} style={{ cursor: 'pointer' }} data-testid="o8-hit-ri">
              <rect x={-50} y={-16} width={100} height={26} rx={4} fill="#1e293b" stroke={done.includes('ri') ? '#22c55e' : nextExpected?.id === 'ri' ? '#fbbf24' : '#334155'} strokeWidth={done.includes('ri') || nextExpected?.id === 'ri' ? 3 : 2} />
              <rect x={-44} y={-11} width={88} height={16} rx={2} fill="#0ea5e9" opacity={0.5} />
              <text y={26} textAnchor="middle" fill="#94a3b8" fontSize={9} fontWeight="bold" fontFamily="monospace">RÉTRO INT.</text>
              {done.includes('ri') && <circle cx={45} cy={-25} r={11} fill="#22c55e" stroke="#0b1120" strokeWidth={2} />}
              {nextExpected?.id === 'ri' && !done.includes('ri') && (
                <circle cx={0} cy={-3} r={40} fill="none" stroke="#fbbf24" strokeWidth={3}>
                  <animate attributeName="r" values="35;50;35" dur="1.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
                </circle>
              )}
            </g>

            {/* Rétro extérieur GAUCHE */}
            {scenario === 'voie' && (
              <g transform="translate(220 200)" onClick={() => clickStep('reg')} style={{ cursor: 'pointer' }} data-testid="o8-hit-reg">
                <path d="M -22 -14 L 12 -10 L 20 6 L 8 16 L -22 10 Z" fill="#1e293b" stroke={done.includes('reg') ? '#22c55e' : nextExpected?.id === 'reg' ? '#fbbf24' : '#334155'} strokeWidth={done.includes('reg') || nextExpected?.id === 'reg' ? 3 : 2} />
                <path d="M -16 -8 L 8 -5 L 12 5 L 4 10 L -16 4 Z" fill="#0ea5e9" opacity={0.55} />
                <text y={36} textAnchor="middle" fill="#94a3b8" fontSize={9} fontWeight="bold" fontFamily="monospace">RÉTRO EXT. G</text>
                {done.includes('reg') && <circle cx={20} cy={-20} r={11} fill="#22c55e" stroke="#0b1120" strokeWidth={2} />}
                {nextExpected?.id === 'reg' && !done.includes('reg') && (
                  <circle cx={0} cy={0} r={40} fill="none" stroke="#fbbf24" strokeWidth={3}>
                    <animate attributeName="r" values="30;45;30" dur="1.4s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            )}

            {/* Angle mort GAUCHE (buste + regard épaule) */}
            {scenario === 'voie' && (
              <g transform="translate(130 320)" onClick={() => clickStep('am')} style={{ cursor: 'pointer' }} data-testid="o8-hit-am">
                <circle r={30} fill="none" stroke={done.includes('am') ? '#22c55e' : nextExpected?.id === 'am' ? '#fbbf24' : '#334155'} strokeWidth={done.includes('am') || nextExpected?.id === 'am' ? 3 : 2} strokeDasharray="5 3" />
                <circle r={12} fill="#f5c9a2" stroke="#0b1120" strokeWidth={1.5} />
                <path d="M 0 12 Q -8 20 -14 22 M 0 12 Q 8 20 14 22" stroke="#f5c9a2" strokeWidth={4} fill="none" strokeLinecap="round" />
                <text y={50} textAnchor="middle" fill="#94a3b8" fontSize={9} fontWeight="bold" fontFamily="monospace">ANGLE MORT G</text>
                <text y={62} textAnchor="middle" fill="#64748b" fontSize={8}>(contrôle-épaule)</text>
                {done.includes('am') && <circle cx={22} cy={-22} r={11} fill="#22c55e" stroke="#0b1120" strokeWidth={2} />}
                {nextExpected?.id === 'am' && !done.includes('am') && (
                  <circle r={45} fill="none" stroke="#fbbf24" strokeWidth={3}>
                    <animate attributeName="r" values="38;55;38" dur="1.4s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            )}

            {/* Clignotant (commodo gauche du volant) */}
            {scenario === 'voie' && (
              <g transform="translate(270 400)" onClick={() => clickStep('cli')} style={{ cursor: 'pointer' }} data-testid="o8-hit-cli">
                <rect x={-32} y={-8} width={40} height={16} rx={8} fill="#3f3f46" stroke={done.includes('cli') ? '#22c55e' : nextExpected?.id === 'cli' ? '#fbbf24' : '#0b1120'} strokeWidth={done.includes('cli') || nextExpected?.id === 'cli' ? 3 : 1.5} />
                <circle cx={-36} cy={0} r={6} fill="#fbbf24">
                  {(done.includes('cli') || nextExpected?.id === 'cli') && <animate attributeName="opacity" values="1;0.2;1" dur="0.5s" repeatCount="indefinite" />}
                </circle>
                <text y={30} textAnchor="middle" fill="#94a3b8" fontSize={9} fontWeight="bold" fontFamily="monospace">CLIGNOTANT G</text>
                {done.includes('cli') && <circle cx={12} cy={-22} r={11} fill="#22c55e" stroke="#0b1120" strokeWidth={2} />}
                {nextExpected?.id === 'cli' && !done.includes('cli') && (
                  <circle cx={-10} cy={0} r={40} fill="none" stroke="#fbbf24" strokeWidth={3}>
                    <animate attributeName="r" values="30;48;30" dur="1.4s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            )}

            {/* Positionnement (indicateur trajectoire) */}
            {scenario === 'voie' && (
              <g transform="translate(560 480)" onClick={() => clickStep('pos')} style={{ cursor: 'pointer' }} data-testid="o8-hit-pos">
                <rect x={-60} y={-30} width={140} height={80} rx={10} fill="#0f172a" stroke={done.includes('pos') ? '#22c55e' : nextExpected?.id === 'pos' ? '#fbbf24' : '#334155'} strokeWidth={done.includes('pos') || nextExpected?.id === 'pos' ? 3 : 2} />
                <path d="M -40 20 Q -10 20 20 -10" stroke="#22c55e" strokeWidth={3} fill="none" strokeDasharray="6 4" />
                <polygon points="20,-10 12,-4 12,-16" fill="#22c55e" />
                <text x={0} y={-10} textAnchor="middle" fill="#94a3b8" fontSize={9} fontWeight="bold" fontFamily="monospace">POSITIONNEMENT</text>
                <text x={0} y={38} textAnchor="middle" fill="#64748b" fontSize={8}>décalage progressif</text>
                {done.includes('pos') && <circle cx={70} cy={-38} r={11} fill="#22c55e" stroke="#0b1120" strokeWidth={2} />}
                {nextExpected?.id === 'pos' && !done.includes('pos') && (
                  <rect x={-64} y={-34} width={148} height={88} rx={12} fill="none" stroke="#fbbf24" strokeWidth={3} strokeDasharray="10 6">
                    <animate attributeName="stroke-dashoffset" values="0;-40" dur="1s" repeatCount="indefinite" />
                  </rect>
                )}
              </g>
            )}

            {/* SÉQUENCE en haut à droite */}
            <g transform="translate(560 40)">
              <rect x={-20} y={-20} width={230} height={140} rx={10} fill="#0f172a" stroke="#334155" strokeWidth={1.5} />
              <text x={-8} y={-4} fill="#94a3b8" fontSize={10} fontFamily="monospace" fontWeight="bold">SÉQUENCE ATTENDUE</text>
              {sc.sequence.map((s, i) => (
                <g key={s.id} transform={`translate(-8 ${10 + i * 22})`}>
                  <circle cx={8} cy={0} r={9} fill={done.includes(s.id) ? '#22c55e' : nextExpected?.id === s.id ? '#fbbf24' : '#334155'} />
                  {done.includes(s.id) ? (
                    <text x={8} y={4} textAnchor="middle" fill="#0b1120" fontSize={10} fontWeight="900">✓</text>
                  ) : (
                    <text x={8} y={4} textAnchor="middle" fill="#fff" fontSize={10} fontWeight="900">{i + 1}</text>
                  )}
                  <text x={22} y={4} fill={done.includes(s.id) ? '#22c55e' : nextExpected?.id === s.id ? '#fbbf24' : '#94a3b8'} fontSize={10} fontFamily="monospace" fontWeight={nextExpected?.id === s.id ? 'bold' : 'normal'}>
                    {s.label}
                  </text>
                </g>
              ))}
            </g>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.65} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">O8 · Séquence des contrôles · {sc.label}</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C1h — Miroir · Signal · Manœuvre</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Progression bar */}
          <div className="p-3.5 border-t border-white/10 bg-black/40">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Progression</span>
              <span className="font-mono-spec text-white text-[13px] font-bold">{done.length}<span className="text-white/40 text-[10px]">/{sc.sequence.length}</span></span>
            </div>
            <div className="w-full h-2.5 rounded-full bg-black/60 border border-white/10 overflow-hidden">
              <div className="h-full transition-all duration-500" style={{ width: `${(done.length / sc.sequence.length) * 100}%`, background: isComplete ? '#22c55e' : sc.color }} />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="o8-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: sc.color, background: sc.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: sc.color }}>Scénario</div>
            <div className="mt-1 font-serif-editorial text-white text-[18px] leading-tight">{sc.label}</div>
            <p className="mt-1.5 text-white/70 text-[12px] leading-relaxed">{sc.context}</p>
          </div>

          {error && (
            <div className="mt-3 rounded-lg border-2 border-red-500 bg-red-950/30 p-3 flex items-start gap-2">
              <X size={16} className="text-red-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Ordre incorrect</div>
                <p className="mt-1 text-white/85 text-[12.5px]">{error}</p>
              </div>
            </div>
          )}

          {isComplete && (
            <div className="mt-3 rounded-lg border-2 border-emerald-500 bg-emerald-950/30 p-3 flex items-start gap-2">
              <CheckCircle2 size={16} className="text-emerald-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Bravo ! Séquence complète</div>
                <p className="mt-1 text-white/90 text-[12.5px]">
                  {scenario === 'allure' ? "Le seul contrôle rétro intérieur suffit pour changer d&apos;allure. Reproduis-le AVANT CHAQUE freinage / accélération." : "Tu as validé la séquence MSM (Miroir-Signal-Manœuvre). Reproduis-la AVANT CHAQUE changement de voie."}
                </p>
              </div>
            </div>
          )}

          {!isComplete && nextExpected && (
            <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
              <Eye size={14} className="text-cyan-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Prochaine étape</div>
                <div className="mt-1 text-white font-semibold text-[13px]">{nextExpected.label}</div>
                <p className="mt-0.5 text-white/70 text-[12px] leading-relaxed">{nextExpected.desc}</p>
              </div>
            </div>
          )}

          <div className="mt-3 rounded-lg border border-purple-500/40 bg-purple-950/20 p-3">
            <div className="text-purple-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1.5">
              <TriangleAlert size={12} /> Moyen mnémotechnique
            </div>
            <p className="text-white/90 text-[12.5px] leading-relaxed">
              <b className="text-purple-200">M · S · M</b> — <b>M</b>iroir → <b>S</b>ignal (clignotant) → <b>M</b>anœuvre (positionnement).
              Sans les 3, la manœuvre n'est pas légale.
            </p>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Piège classique</div>
              <p className="mt-1 text-white/85 text-[12px] leading-relaxed">
                Mettre le clignotant EN MÊME TEMPS que le début du décalage = trop tard. Le clignotant AVANT (3 s min) — c&apos;est un signal, pas une conséquence.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
